/**
 * Orchestrates the save-confirmation dialog for direct interview edits.
 *
 * - Captures a change snapshot once when edit data is hydrated (prefillVersion > 0).
 * - `requestSave(onConfirmed)` computes the diff and either:
 *     • calls onConfirmed immediately (no changes, or not an edit, or self-schedule)
 *     • opens the dialog and defers onConfirmed until the user confirms
 * - Dialog is only shown for `isEdit && !isSelfSchedule`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { useKFlags } from '@/hooks/use-k-flags';
import type {
  ScheduleInterviewPayload,
  InterviewUpdateFlags,
} from '../config/types';
import type { RecipientInfo } from './SaveConfirmationDialog';
import { buildChangeSnapshot } from './buildChangeSnapshot';
import {
  computeUpdateFlags,
  type SaveConfirmationVariant,
} from './useChangeTracking';
import { getCachedInterviewerOption } from '../state/interviewerOptionCache';

const NO_FLAGS: InterviewUpdateFlags = {
  update_calendar_invite_to_candidate: false,
  update_calendar_invite_to_interviewer: false,
  resend_email_to_candidate: false,
  resend_email_to_interviewer: false,
};

interface UseEditConfirmationArgs {
  payloadRef: MutableRefObject<ScheduleInterviewPayload>;
  prefillVersion: number;
  isEdit: boolean;
  isSelfSchedule: boolean;
}

export interface EditConfirmationState {
  /** Intercepts save — shows dialog when needed, calls onConfirmed directly otherwise. */
  requestSave: (_onConfirmed: (_flags: InterviewUpdateFlags) => void) => void;
  /** Call when the user clicks "Save Interview" in the dialog. */
  confirmDialog: () => void;
  /** Call when the user clicks "Cancel" in the dialog. */
  cancelDialog: () => void;
  /** Non-null when dialog should be rendered. */
  dialogVariant: SaveConfirmationVariant;
  isDialogOpen: boolean;
  /** Flags computed for the pending save — used to filter which recipients to show in the dialog. */
  dialogFlags: InterviewUpdateFlags;
  /** Current selected interviewers, snapshotted at save time. */
  dialogInterviewers: RecipientInfo[];
}

export function useEditConfirmation({
  payloadRef,
  prefillVersion,
  isEdit,
  isSelfSchedule,
}: UseEditConfirmationArgs): EditConfirmationState {
  const { tGoogleMeetExternalHost } = useKFlags() as {
    tGoogleMeetExternalHost?: boolean;
  };
  const originalSnapshot = useRef(
    buildChangeSnapshot(payloadRef.current, tGoogleMeetExternalHost)
  );
  const snapshotCaptured = useRef(false);
  const pendingSave = useRef<((_flags: InterviewUpdateFlags) => void) | null>(
    null
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogVariant, setDialogVariant] =
    useState<SaveConfirmationVariant>(null);
  const [pendingFlags, setPendingFlags] =
    useState<InterviewUpdateFlags>(NO_FLAGS);
  const [dialogInterviewers, setDialogInterviewers] = useState<RecipientInfo[]>(
    []
  );

  // Capture the original snapshot once when edit hydration fires.
  useEffect(() => {
    if (prefillVersion > 0 && !snapshotCaptured.current) {
      snapshotCaptured.current = true;
      originalSnapshot.current = buildChangeSnapshot(
        payloadRef.current,
        tGoogleMeetExternalHost
      );
    }
  }, [prefillVersion, payloadRef, tGoogleMeetExternalHost]);

  const requestSave = useCallback(
    (onConfirmed: (_flags: InterviewUpdateFlags) => void) => {
      if (!isEdit || isSelfSchedule) {
        onConfirmed(NO_FLAGS);
        return;
      }

      const current = buildChangeSnapshot(
        payloadRef.current,
        tGoogleMeetExternalHost
      );
      const { flags, variant } = computeUpdateFlags(
        originalSnapshot.current,
        current
      );

      if (!variant) {
        onConfirmed(NO_FLAGS);
        return;
      }

      // Snapshot selected interviewers now so the dialog shows the current
      // roster, not the stale prefilled options.
      const opts = payloadRef.current.details.participant_default_options as
        | Array<{ value?: string; label?: string; image?: string }>
        | undefined;
      const selectedIds = (() => {
        const raw = payloadRef.current.details.participant_ids;
        if (Array.isArray(raw)) return raw.map(String);
        if (raw) return [String(raw)];
        return [];
      })();
      // Build a lookup from the hydrated default options, then fall back
      // to the module-level cache for interviewers loaded via async search.
      const defaultOptsMap = new Map(
        (opts ?? []).map((o) => [String(o.value ?? ''), o])
      );
      const snapshotInterviewers: RecipientInfo[] = selectedIds.map((id) => {
        const fromDefaults = defaultOptsMap.get(id);
        if (fromDefaults) {
          return { name: fromDefaults.label ?? '', avatar: fromDefaults.image };
        }
        const fromCache = getCachedInterviewerOption(id);
        return { name: fromCache?.label ?? id, avatar: fromCache?.image };
      });

      pendingSave.current = onConfirmed;
      setPendingFlags({ ...flags });
      setDialogInterviewers(snapshotInterviewers);
      setDialogVariant(variant);
      setIsDialogOpen(true);
    },
    [isEdit, isSelfSchedule, payloadRef, tGoogleMeetExternalHost]
  );

  const confirmDialog = useCallback(() => {
    setIsDialogOpen(false);
    const save = pendingSave.current;
    pendingSave.current = null;
    save?.({ ...pendingFlags });
  }, [pendingFlags]);

  const cancelDialog = useCallback(() => {
    pendingSave.current = null;
    setIsDialogOpen(false);
  }, []);

  return {
    requestSave,
    confirmDialog,
    cancelDialog,
    dialogVariant,
    isDialogOpen,
    dialogFlags: pendingFlags,
    dialogInterviewers,
  };
}
